import React from "react";
import {
  Building2,
  Edit,
  Pause,
  Play,
  Trash2,
  Headphones,
} from "lucide-react";
import { Question, QuestionsMode, UserRole } from "../../types";
import { useAuth } from "../../contexts/AuthContext";

type Props = {
  mode: QuestionsMode;
  questions: Question[];
  question: Question;
  index: number;
  total: number;
  language: "es" | "ca" | "en" | "ar";
  currentPlayingId: string | null;
  setCurrentPlayingId: (id: string | null) => void;
  audioRefs: React.MutableRefObject<Record<string, HTMLAudioElement | null>>;
  handleEditQuestion: (q: Question) => void;
  handleDeleteQuestion: (questionId: string) => void;
  moveQuestion: (id: string, direction: "up" | "down") => void;
  setNotification: React.Dispatch<
    React.SetStateAction<{
      type: "success" | "error" | "warning" | "info";
      title: string;
      message?: string;
      actions?: import("../Notification").NotificationAction[];
    } | null>
  >;
};

export default function QuestionItem({
  mode,
  question,
  index,
  total: _total,
  language: _language,
  currentPlayingId,
  setCurrentPlayingId,
  audioRefs,
  handleEditQuestion,
  handleDeleteQuestion,
  moveQuestion,
  setNotification,
  questions,
}: Props) {
  const { translations, role, language } = useAuth();
  const isGeneric = mode.kind === "generic";
  const lockedForOrganization = role === UserRole.Organization && isGeneric;

  const [objectUrls, setObjectUrls] = React.useState<
    Record<"es" | "ca" | "en" | "ar", string | null>
  >({
    es: null,
    ca: null,
    en: null,
    ar: null,
  });

  React.useEffect(() => {
    const langs: Array<"es" | "ca" | "en" | "ar"> = ["es", "ca", "en", "ar"];
    const created: string[] = [];
    const next: Record<"es" | "ca" | "en" | "ar", string | null> = {
      es: null,
      ca: null,
      en: null,
      ar: null,
    };
    langs.forEach((lng) => {
      // Check both audioFiles (local) and audioUrls (from API)
      const file = question.audioFiles?.[lng] ?? null;
      const url = question.audioUrls?.[lng] ?? null;
      
      if (file) {
        const u = URL.createObjectURL(file);
        next[lng] = u;
        created.push(u);
      } else if (url) {
        // Use the blob URL from API directly
        next[lng] = url;
      }
    });
    setObjectUrls(next);
    return () => {
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [
    question.audioFiles?.es,
    question.audioFiles?.ca,
    question.audioFiles?.en,
    question.audioFiles?.ar,
    question.audioUrls?.es,
    question.audioUrls?.ca,
    question.audioUrls?.en,
    question.audioUrls?.ar,
  ]);

  // Check if any audio is available for any language
  const hasAnyAudio = React.useMemo(() => {
    const langs: Array<"es" | "ca" | "en" | "ar"> = ["es", "ca", "en", "ar"];
    return langs.some((lang) => {
      // Check audioFiles (local files)
      if (question.audioFiles?.[lang]) return true;
      
      // Check audioUrls (can be blob URLs or API URLs like "voice/u0zeg10w.mp3")
      const audioUrl = question.audioUrls?.[lang];
      if (audioUrl && audioUrl.trim() && audioUrl.trim() !== "") {
        return true;
      }
      
      // Check audioObsKeys (API URLs like "voice/u0zeg10w.mp3")
      const audioObsKey = question.audioObsKeys?.[lang];
      if (audioObsKey && audioObsKey.trim() && audioObsKey.trim() !== "") {
        return true;
      }
      
      return false;
    });
  }, [question.audioFiles, question.audioUrls, question.audioObsKeys]);

  const orgName =
    question.organizationName ||
    undefined;
  return (
    <div
      key={question.id}
      className="relative flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors overflow-hidden"
    >
      {/* Audio Ribbon Banner - Esquina superior izquierda */}
      {hasAnyAudio && (
        <div className="absolute top-0 left-0 w-20 bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md z-10 transform -rotate-45 -translate-x-6 translate-y-2.5 flex items-center justify-center py-1">
          <Headphones className="w-3.5 h-3.5" />
        </div>
      )}
      {/* Drag Handle */}
      {/* <div className="flex flex-col items-center space-y-1 pt-2">
        <GripVertical className="w-4 h-4 text-gray-400" />
        <span className="text-xs text-gray-500 font-medium">
          {question.order}
        </span>
      </div> */}

      {/* Question Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-2">
              <div className="flex-1">
                {/* All translations displayed */}
                <div className="space-y-2.5">
                  {(["es", "ca", "en", "ar"] as const).map((lang) => {
                    const text = question.text[lang];
                    if (!text) return null;
                    
                    const hasAudioForLang = !!(
                      question.audioFiles?.[lang] ||
                      (question.audioUrls?.[lang] && question.audioUrls[lang].trim() && question.audioUrls[lang].trim() !== "") ||
                      (question.audioObsKeys?.[lang] && question.audioObsKeys[lang].trim() && question.audioObsKeys[lang].trim() !== "")
                    );
                    const src = objectUrls[lang];
                    const isLangPlaying = currentPlayingId === `${question.id}-${lang}`;
                    const langLabel = 
                      lang === "es" ? "ES" :
                      lang === "ca" ? "CA" :
                      lang === "en" ? "EN" :
                      "AR";

                    return (
                      <div key={lang} className="flex items-center gap-3 group">
                        <span className="text-xs font-semibold text-gray-600 min-w-[36px]">
                          {langLabel}:
                        </span>
                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          <p className="text-sm text-gray-900 break-words flex-1">
                            {text}
                          </p>
                          {/* Botón de reproducir oculto */}
                          {/* {hasAudioForLang && src && (
                            <button
                              type="button"
                              onClick={() => {
                                const currentAudio =
                                  audioRefs.current[`${question.id}-${lang}`];
                                if (!currentAudio) return;
                                if (isLangPlaying) {
                                  currentAudio.pause();
                                  setCurrentPlayingId(null);
                                } else {
                                  Object.values(audioRefs.current).forEach((a) =>
                                    a?.pause()
                                  );
                                  setCurrentPlayingId(`${question.id}-${lang}`);
                                  currentAudio
                                    .play()
                                    .catch(() => setCurrentPlayingId(null));
                                }
                              }}
                              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-all duration-200 active:scale-95"
                              aria-label={
                                isLangPlaying
                                  ? translations.pause ?? "Pausar"
                                  : translations.play ?? "Reproducir"
                              }
                              title={
                                isLangPlaying
                                  ? translations.pause ?? "Pausar"
                                  : translations.play ?? "Reproducir"
                              }
                            >
                              {isLangPlaying ? (
                                <Pause className="w-3.5 h-3.5" />
                              ) : (
                                <Play className="w-3.5 h-3.5 ml-0.5" />
                              )}
                            </button>
                          )} */}
                          {/* {hasAudioForLang && !src && (
                            <Headphones className="w-4 h-4 text-gray-400 flex-shrink-0" 
                              aria-label={translations.audioAvailable ?? "Audio disponible"} />
                          )} */}
                          {hasAudioForLang && src && (
                            <audio
                              ref={(el) =>
                                (audioRefs.current[`${question.id}-${lang}`] = el)
                              }
                              src={src}
                              onEnded={() => setCurrentPlayingId(null)}
                              onError={() => setCurrentPlayingId(null)}
                              className="hidden"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Badge for entity (only for specific questions) */}
            {!isGeneric && orgName && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                  <Building2 className="w-3 h-3 mr-1.5" />
                  {orgName}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          {lockedForOrganization ? (
            <></>
          ) : (
            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={() => handleEditQuestion(question)}
                className="p-1 text-blue-600 hover:text-blue-800"
                title={translations.edit}
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setNotification({
                    type: "warning",
                    title: translations.confirmDelete,
                    message: translations.actionCannotBeUndone,
                    actions: [
                      {
                        label: translations.yesDelete,
                        onClick: async (_close) => {
                          await handleDeleteQuestion(question?.id);
                        },
                        className: "bg-red-600 text-white",
                      },
                      {
                        label: translations.cancel,
                        onClick: (close) => {
                          close();
                        },
                        className: "bg-gray-300 text-black",
                      },
                    ],
                  });
                }}
                className="p-1 text-red-600 hover:text-red-800"
                title={translations.delete}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Date */}
        {question.createdAt && (
          <div className="mt-2 flex items-center space-x-1">
            <span className="text-xs text-gray-500">
              {translations.createdAt ?? "Fecha de creación"}:
            </span>
            <span className="text-xs text-gray-700">
              {new Date(question.createdAt).toLocaleDateString(
                language === "ca" ? "ca-ES" : "es-ES",
                {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                }
              )}
            </span>
          </div>
        )}

        {/* Rating Preview */}
        <div className="mt-3 flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {translations.ratingScale}:
          </span>
          <div className="flex space-x-1">
            {[1, 2, 3, 4, 5].map((rating) => (
              <div
                key={rating}
                className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-medium text-gray-600"
              >
                {rating}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
